import { createSelector } from '@reduxjs/toolkit';

import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { shouldConditionItemBeShown } from '@/shared/embeddedLibs/conditionsAndMasks';

import { composedModel } from '@/entities/composed';
import { conditionModel, getToothStatus } from '@/entities/condition';
import { organizationModel } from '@/entities/organization';
import { isReport2D, reportsModel } from '@/entities/reports';
import {
  LOWER_JAW_TEETH_ISO_NUMBERS,
  toothModel,
  ToothTypesSet,
} from '@/entities/tooth';

import { DEFAULT_TOOTH_CHART_ITEMS, PBL_SEVERITIES } from '../config/constants';
import { ToothItem } from '../config/types';

const LOWER_JAW_TEETH_ISO_NUMBERS_SET = LOWER_JAW_TEETH_ISO_NUMBERS.reduce(
  (acc, number) => {
    acc[number] = number;
    return acc;
  },
  {} as Record<number, number>,
);

// Handle supernumerary and primary teeth
const groupTeethByISONumber = (teeth: Tooth[]) =>
  teeth.reduce(
    (acc, tooth) => {
      const ISO = tooth.Numeration?.ISO as number;

      if (!acc[ISO]) {
        acc[ISO] = [];
      }
      acc[ISO].push(tooth);

      return acc;
    },
    {} as Record<number, typeof teeth>,
  );

export const selectPBLSeverities = (toothID: string) =>
  createSelector(
    composedModel.selectors.selectToothActiveLogicalConditions(toothID),
    reportsModel.selectors.selectLowProbabilityMode,
    (toothConditions, showLowProbability) => {
      const pblConditions = toothConditions
        .flatMap((condition) => [condition, ...condition.Children])
        .filter(
          (condition) =>
            condition.ParentCode === ConditionCode.PeriodontalBoneLoss,
        );

      return PBL_SEVERITIES.map((severity) =>
        pblConditions.some(
          (condition) =>
            condition.Code === severity &&
            shouldConditionItemBeShown(showLowProbability)(condition),
        ),
      );
    },
  );

const getGridArea = (ISONumber: number) => {
  if (ISONumber > 100 || ISONumber < 50) {
    return ISONumber;
  } else {
    return ISONumber - 40;
  }
};

export const selectToothChartItems = (
  reportID: string,
  isPreviewMode = false,
) =>
  createSelector(
    toothModel.selectors.selectByReportID(reportID),
    conditionModel.selectors.selectGroupedByToothIDConditions,
    toothModel.selectors.selectLocalROITeethIDs,
    reportsModel.selectors.selectLowProbabilityMode,
    organizationModel.selectors.selectFDAEnvironment,
    reportsModel.selectors.selectCurrentReportType,
    (
      teeth,
      groupdeByToothIDConditions,
      localROITeethIDs,
      showLowProbability,
      fdaEnvironment,
      currentReportType,
    ) => {
      const isGPReport = currentReportType === ReportType.ReportType_CBCT_GP;
      const { isFDA, treatToothLikeAsTooth: isFDANonAided } = fdaEnvironment;
      const isFDA3DGPRestrictions =
        fdaEnvironment.isFDA3DGPRestrictions && isGPReport;
      const shouldTreatAllTeethAsActive = fdaEnvironment.isFDAVersion2;
      const notRemovedTeeth = teeth.filter((tooth) => !tooth.IsRemoved);
      const shouldShowLowProbability = showLowProbability && !isPreviewMode;

      const toothStatusConfig = {
        showLowProbability: shouldShowLowProbability,
        isFDA,
        isFDANonAided,
        ignoreLowProbability: isPreviewMode,
        isFDA3DGPRestrictions,
        isFDAVersion2: fdaEnvironment.isFDAVersion2,
        is2DReport: isReport2D(currentReportType),
      };

      const toothTypesSet = {} as ToothTypesSet;

      const toothGroupedByISONumber = groupTeethByISONumber(notRemovedTeeth);

      const toothItems = Object.values(DEFAULT_TOOTH_CHART_ITEMS).reduce(
        (acc, toothItem) => {
          const { ISONumber } = toothItem;
          const [firstTooth, ...restTeeth] =
            toothGroupedByISONumber[ISONumber]?.sort(
              (a, b) =>
                (a?.Numeration?.SupernumeraryIndex ?? 0) -
                (b?.Numeration?.SupernumeraryIndex ?? 0),
            ) ?? [];

          // Collect supernumeric or primary teeth
          const extraTeeth = (
            toothGroupedByISONumber[ISONumber + 40] || restTeeth
          )
            // NOTE: Tooth without localizatons means there is nothing to show, because it's not has any conditions.
            // It could be happened after changing tooth number.
            .filter((tooth) => tooth.Detections.length)
            .map((tooth) => {
              const toothStatus = getToothStatus(
                groupdeByToothIDConditions[tooth.ID] ?? [],
                toothStatusConfig,
              );
              toothTypesSet[tooth.ID] = toothStatus;

              return {
                id: tooth.ID,
                isApproved: tooth.IsApproved,
                status: toothStatus,
                isRemoved: tooth.IsRemoved,
                ISONumber: tooth?.Numeration?.ISO as number,
                supernumeraryIndex: tooth.Numeration?.SupernumeraryIndex,
                active:
                  shouldTreatAllTeethAsActive ||
                  localROITeethIDs.includes(tooth.ID),
                isLowerJaw: ISONumber in LOWER_JAW_TEETH_ISO_NUMBERS_SET,
                gridArea: getGridArea(tooth?.Numeration?.ISO as number),
              };
            });

          if (firstTooth) {
            const toothStatus = getToothStatus(
              groupdeByToothIDConditions[firstTooth.ID] ?? [],
              toothStatusConfig,
            );
            toothTypesSet[firstTooth.ID] = toothStatus;

            acc.push({
              id: firstTooth.ID,
              status: toothStatus,
              isApproved: firstTooth.IsApproved,
              ISONumber,
              isRemoved: firstTooth.IsRemoved,
              isLowerJaw: toothItem.isLowerJaw,
              active:
                shouldTreatAllTeethAsActive ||
                localROITeethIDs.includes(firstTooth.ID),
              childItems: extraTeeth,
              gridArea: getGridArea(ISONumber),
            });
          } else if (extraTeeth.length > 0) {
            acc.push(extraTeeth[0]);
          } else {
            acc.push(toothItem);
          }

          return acc;
        },
        [] as ToothItem[],
      );

      return {
        toothItems,
        toothTypesSet,
        isFDA3DGPRestrictions,
      };
    },
  );
